import { zip } from 'lodash';
import { GAUGE_CHART_EPSILON } from '@/components';
import { GenericThresholdOptions, StepOptions, ThresholdColorPalette, ThresholdOptions } from '@/core';

export type GaugeColorStop = [number, string];

export type EChartsAxisLineColors = GaugeColorStop[];

export const defaultThresholdInput: ThresholdOptions = { steps: [{ value: 0 }] };

export function convertThresholds(
  thresholds: GenericThresholdOptions,
  max: number,
  palette: ThresholdColorPalette
): EChartsAxisLineColors {
  // Attempt to use base.color as the first default color
  const defaultThresholdColor = thresholds.base?.color ?? thresholds.default_color ?? palette.defaultColor;
  const defaultThresholdSteps: EChartsAxisLineColors = [[0, defaultThresholdColor]];

  if (thresholds.steps !== undefined) {
    // https://echarts.apache.org/en/option.html#series-gauge.axisLine.lineStyle.color
    // color segments must be decimal between 0 and 1
    const segmentMax = 1;
    let valuesArr: number[] = thresholds.steps.map((step: StepOptions) => {
      if (thresholds.mode === 'Percent') {
        return step.value / 100;
      }
      return step.value / max;
    });

    const maxValue = valuesArr.reduce((currentMax, value) => Math.max(currentMax, value), 0);

    // Since echarts does greater than, and we want to do greater than/ Equal to, we subtract this tiny epsilon
    // value from every value
    valuesArr = valuesArr.map((value) => {
      // If the value is already really small (ie, less than epsilon) then we just take 99% of it
      if (value <= GAUGE_CHART_EPSILON) {
        return value * 0.99;
      } else {
        return value - GAUGE_CHART_EPSILON;
      }
    });
    // We need to finish the chart to 1 (segment max) or to the maxValue so push those on
    valuesArr.push(Math.max(segmentMax, maxValue));

    const colorsArr = thresholds.steps.map((step: StepOptions, index) => step.color ?? palette.palette[index]);
    colorsArr.unshift(defaultThresholdColor);

    const zippedArr = zip(valuesArr, colorsArr);
    return zippedArr.map((elem) => {
      const convertedValues = elem[0] ?? segmentMax;
      const convertedColors = elem[1] ?? defaultThresholdColor;
      return [convertedValues, convertedColors];
    });
  } else {
    return defaultThresholdSteps;
  }
}
